import { SelectInput, useDialog } from "@repo/ui";
import { useGetChoiceUsage } from "~/pages/administration/workflows-page/workflows-dialog/views/metadata-view/metadata-input/use-get-choice-usage";
import type { FieldType } from "~/types/api";
import { ChoicesInput } from "./choices-input";
import type { FieldTypeOption } from "./metadata-input";

type Option = { value: string; default: boolean };

const CHOICE_TYPES = [
  { label: "String", value: "String" },
  { label: "Integer", value: "Integer" },
];

type Props = {
  type: FieldType;
  options: Option[];
  saveCount: number;
  error?: string;
  workflowId: number | null;
  fieldId: number | null;
  onTypeChange: (newType: FieldType, clearedOptions?: Option[]) => void;
  onOptionsChange: (options: Option[]) => void;
  onErrorClear: () => void;
  isUpgradeMode?: boolean;
};

export const ChoicesSection = ({
  type,
  options,
  saveCount,
  error,
  workflowId,
  fieldId,
  onTypeChange,
  onOptionsChange,
  onErrorClear,
  isUpgradeMode,
}: Props) => {
  const { showDialog, closeDialog } = useDialog();
  const getChoiceUsage = useGetChoiceUsage(workflowId, fieldId);

  const handleChoiceTypeChange = (value: string | string[] | undefined) => {
    if (typeof value !== "string") return;

    if (value === "Integer") {
      const nonNumericChoices = options.filter(
        (c) => c.value.trim() !== "" && isNaN(Number(c.value))
      );
      if (nonNumericChoices.length > 0) {
        showDialog({
          title: "Clear Non-Numeric Choices?",
          description:
            "Some of your choices contain non-numeric values that are not valid for Integer type. Switching will clear all current choices.",
          confirmText: "Switch & Clear",
          confirmVariant: "destructive",
          cancelText: "Keep String",
          cancelVariant: "outline",
          disableXClose: true,
          onConfirm: () => {
            closeDialog();
            onTypeChange(value as FieldType, []);
          },
        });
        return;
      }
    }

    onTypeChange(value as FieldType);
  };

  const handleTryDeleteChoice = async (choice: string): Promise<boolean> => {
    if (!isUpgradeMode) return true;
    return getChoiceUsage(choice);
  };

  return (
    <>
      <SelectInput
        data-allow-navigation
        label="Choice Type"
        options={CHOICE_TYPES}
        value={type}
        clearable={false}
        onChange={handleChoiceTypeChange}
      />
      <ChoicesInput
        key={saveCount}
        error={error}
        type={type as FieldTypeOption}
        initialChoices={options.map((c) => c.value)}
        onChoicesChange={(choices) => {
          onOptionsChange(choices.map((c) => ({ value: c, default: false })));
          onErrorClear();
        }}
        onTryDeleteChoice={handleTryDeleteChoice}
      />
    </>
  );
};
